// ============================================
// In-app APK self-update: onboarding + update banner (Android / Capacitor)
// ============================================
// Mirrors desktop-update.js UX for the Android shell: one-time first-launch
// onboarding, a Settings toggle and an on-brand in-game banner. The mechanics
// differ from desktop (there is no shell HTTP server), so JS drives the whole
// flow via the custom ApkInstaller plugin: check /api/updates/check/android
// (entitlement Bearer on the patreon channel), native download with progress
// events, then Android's package installer applies the new signed APK (the
// player confirms once; signature + versionCode are enforced by the OS, saves
// survive because the app id stays the same). Patreon pairing: entitlement.js
// stores the refresh handle in localStorage (PnO_Entitlement) on Android; it
// is exchanged for a short-lived access token per check, so an unpaired or
// revoked install silently stays frozen (SELF_UPDATE.md anti-piracy goal).
// No-op outside the Android app.

import { L } from '../systems/localization.js';
import { isPat } from '../engine/pgate.js';
import { VERSION_INFO } from './changelog.js';

const API = 'https://www.pinkedandowned.com';
const BAR_ID = 'pno-update-bar';
const ONBOARD_ID = 'pno-onboard-bar';

function isAndroidApp() {
    try { return !!(window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'android'); }
    catch (e) { return false; }
}
function plugin() {
    try { return (window.Capacitor.Plugins && window.Capacitor.Plugins.ApkInstaller) || null; }
    catch (e) { return null; }
}
function t(key, en) {
    try { const v = L.get(key); return (v && v !== key) ? v : en; }
    catch (e) { return en; }
}
function ls(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
function autoUpdateOn() { return ls('PnO_AutoUpdate') === 'on'; }

let _phase = 'none';   // none|offered|downloading|ready|error|dismissed
let _update = null;    // last check result {version, filename, size, url}
let _apkPath = null;   // downloaded file, waiting for the installer
let _auth = null;      // Authorization header used for the gated download

export function initAndroidUpdate() {
    if (!isAndroidApp() || !plugin()) return;
    try { plugin().cleanup().catch(() => {}); } catch (e) {} // drop APKs from a previous update
    injectSettingsToggle();
    try { window.addEventListener('pno-paired', () => { check(); }); } catch (e) {}
    if (ls('PnO_UpdateOnboarded') !== '1') { runOnboarding(); return; }
    if (autoUpdateOn()) check();
}

// ── DOM helpers (same look as desktop-update.js) ────────────────────────────
function el(tag, attrs, ...kids) {
    const n = document.createElement(tag);
    for (const k in (attrs || {})) {
        if (k === 'class') n.className = attrs[k];
        else if (k === 'text') n.textContent = attrs[k];
        else if (k === 'style') n.style.cssText = attrs[k];
        else n.setAttribute(k, attrs[k]);
    }
    kids.forEach(c => n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c));
    return n;
}
function txt(s) { return el('span', { text: s }); }
function mkBtn(label, primary) {
    return el('button', {
        text: label,
        style: 'padding:6px 16px;border-radius:8px;border:none;cursor:pointer;'
            + 'font-family:inherit;font-size:0.82rem;font-weight:700;'
            + (primary ? 'background:#fff;color:#7c3aed;' : 'background:rgba(255,255,255,0.18);color:#fff;'),
    });
}
function topBar(id, gradient) {
    let b = document.getElementById(id);
    if (!b) { b = el('div', { id }); document.body.appendChild(b); }
    // padding-top: the Android WebView draws edge-to-edge under the system
    // status bar, and that strip is NOT clickable — a top:0 banner put its
    // text/buttons right beneath it (owner report 2026-07-11). Reserve the
    // inset via env(safe-area-inset-top) with a 34px floor for WebViews
    // that report a 0 inset without viewport-fit=cover.
    b.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483000;'
        + `background:${gradient};color:#fff;`
        + 'padding:max(calc(9px + env(safe-area-inset-top, 0px)), 34px) 16px 9px;'
        + 'display:flex;align-items:center;'
        + 'justify-content:center;gap:14px;flex-wrap:wrap;font-family:Inter,system-ui,sans-serif;'
        + 'font-size:0.9rem;box-shadow:0 3px 16px rgba(0,0,0,0.35)';
    b.innerHTML = '';
    return b;
}
function removeBar(id) { const b = document.getElementById(id); if (b) b.remove(); }

// Capacitor opens external http(s) top-frame navigations in the system
// browser (the WebView itself never leaves the game), so location.href is the
// reliable "open externally" call here; target=_blank anchors are not.
function openExternal(url) {
    try { window.location.href = url; } catch (e) {}
}

// ── first-launch onboarding (shown once, same keys as desktop) ──────────────
function runOnboarding() {
    const b = topBar(ONBOARD_ID, 'linear-gradient(135deg,#8b5cf6,#a855f7)');
    b.appendChild(txt(t('onboard.notify', 'Notify you here when a new update is available?')));
    const yes = mkBtn(t('onboard.yes', 'Yes'), true);
    yes.onclick = () => { lsSet('PnO_AutoUpdate', 'on'); finishOnboarding(true); };
    const no = mkBtn(t('onboard.no', 'No'), false);
    no.onclick = () => { lsSet('PnO_AutoUpdate', 'off'); finishOnboarding(false); };
    b.appendChild(yes); b.appendChild(no);
}

function finishOnboarding(on) {
    lsSet('PnO_UpdateOnboarded', '1');
    syncSettingsToggle();
    // On a Patreon build, nudge to connect (updates are gated on a linked account).
    if (on && isPat() && !pairedHandle()) { showConnectNudge(); check(); return; }
    removeBar(ONBOARD_ID);
    if (on) check();
}

function showConnectNudge() {
    const b = topBar(ONBOARD_ID, 'linear-gradient(135deg,#ec4899,#a855f7)');
    b.appendChild(txt(t('onboard.connect', 'Connect your Patreon account to receive updates (Settings has it too).')));
    const connect = mkBtn(t('entitlement.connect_patreon', 'Connect Patreon'), true);
    connect.onclick = () => openExternal(`${API}/api/patreon/login?flow=app`);
    const later = mkBtn(t('onboard.later', 'Later'), false);
    later.onclick = () => removeBar(ONBOARD_ID);
    b.appendChild(connect); b.appendChild(later);
}

// ── Settings toggle (same id/keys as the desktop module; only one runs) ─────
function injectSettingsToggle() {
    const host = document.getElementById('menu-settings');
    if (!host || document.getElementById('pno-autoupdate-row')) return;
    const row = el('div', { id: 'pno-autoupdate-row', class: 'settings-row',
        style: 'margin-top:8px;align-items:center;gap:10px' });
    row.appendChild(el('span', { class: 'settings-label',
        text: t('settings.updatenotify', 'Update notifications') }));
    const cb = el('input', { type: 'checkbox', id: 'pno-autoupdate-cb' });
    cb.checked = autoUpdateOn();
    cb.onchange = () => {
        lsSet('PnO_AutoUpdate', cb.checked ? 'on' : 'off');
        lsSet('PnO_UpdateOnboarded', '1');
        if (cb.checked) {
            if (_phase === 'dismissed') _phase = 'none'; // allow a fresh offer
            check();
        } else { removeBar(BAR_ID); _phase = 'dismissed'; }
    };
    row.appendChild(cb);
    host.appendChild(row);
}
function syncSettingsToggle() {
    const cb = document.getElementById('pno-autoupdate-cb');
    if (cb) cb.checked = autoUpdateOn();
}

// ── entitlement (patreon channel) ───────────────────────────────────────────
function pairedHandle() {
    try { return (JSON.parse(ls('PnO_Entitlement') || 'null') || {}).refresh_handle || null; }
    catch (e) { return null; }
}

async function getAccessToken() {
    const handle = pairedHandle();
    if (!handle) return null;
    try {
        const r = await fetch(`${API}/api/entitlement/refresh`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_handle: handle }),
        });
        if (!r.ok) return null; // revoked/lapsed (401) or transient -> no patreon updates this run
        return (await r.json()).access_token || null;
    } catch (e) { return null; }
}

// ── update flow ─────────────────────────────────────────────────────────────
async function check() {
    if (!autoUpdateOn() || _phase === 'downloading' || _phase === 'ready') return;
    const channel = isPat() ? 'patreon' : 'free';
    _auth = null;
    if (channel === 'patreon') {
        const tok = await getAccessToken();
        if (!tok) return; // unpaired / revoked -> stay frozen (anti-piracy gate)
        _auth = `Bearer ${tok}`;
    }
    let j;
    try {
        const headers = _auth ? { Authorization: _auth } : {};
        const r = await fetch(
            `${API}/api/updates/check/android?current=${VERSION_INFO.version}&channel=${channel}`,
            { headers, cache: 'no-store' });
        if (!r.ok) return;
        j = await r.json();
    } catch (e) { return; }
    if (!j || !j.update_available) return;
    _update = j;
    if (_phase === 'none' || _phase === 'error') render('offered');
}

async function startDownload() {
    if (!_update || !plugin()) return;
    _phase = 'downloading';
    render('downloading', { loaded: 0, total: _update.size || 0 });
    let sub = null;
    try {
        sub = await plugin().addListener('downloadProgress', (p) => {
            if (_phase === 'downloading') render('downloading', p);
        });
        const res = await plugin().download({
            url: `${API}${_update.url}`,
            filename: _update.filename || 'update.apk',
            auth: _auth || '',
        });
        _apkPath = res && res.path;
        _phase = 'ready';
        render('ready');
        install(); // hand off to the system installer right away
    } catch (e) {
        console.warn('[AndroidUpdate] download failed:', e && e.message);
        _phase = 'error';
        render('error');
    } finally {
        try { if (sub) sub.remove(); } catch (e) {}
    }
}

async function install() {
    if (!_apkPath || !plugin()) return;
    try {
        await plugin().install({ path: _apkPath });
        // The system installer UI takes over; keep the "ready" banner so the
        // player can tap Install again if they cancel it.
    } catch (e) {
        if (e && /not-allowed/.test(e.message || '')) {
            render('ready', true); // settings screen opened; explain + retry
        } else {
            console.warn('[AndroidUpdate] install failed:', e && e.message);
            _phase = 'error';
            render('error');
        }
    }
}

// ── banner ──────────────────────────────────────────────────────────────────
function render(phase, extra) {
    if (phase === 'offered') {
        _phase = 'offered';
        const mb = Math.round((_update.size || 0) / 1048576);
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#8b5cf6,#a855f7)');
        b.appendChild(txt(t('update.available', 'Update available') + `: v${_update.version} (${mb} MB)`));
        const now = mkBtn(t('update.now', 'Update now'), true);
        now.onclick = () => { startDownload(); };
        const later = mkBtn(t('update.later', 'Later'), false);
        later.onclick = () => { _phase = 'dismissed'; removeBar(BAR_ID); };
        b.appendChild(now); b.appendChild(later);
    } else if (phase === 'downloading') {
        const total = (extra && extra.total) ? extra.total : 0;
        const loaded = (extra && extra.loaded) ? extra.loaded : 0;
        const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : null;
        const base = t('update.downloading', 'Downloading update');
        const line = pct === null ? base + '...'
            : `${base} ${pct}% (${Math.round(loaded / 1048576)} / ${Math.round(total / 1048576)} MB)`;
        // Update in place while the bar exists (same pattern as desktop).
        const label = document.getElementById('pno-update-dltext');
        const fill = document.getElementById('pno-update-fill');
        if (label && fill && pct !== null) {
            label.textContent = line;
            fill.style.width = pct + '%';
            return;
        }
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#8b5cf6,#a855f7)');
        if (pct === null) {
            b.appendChild(txt(line));
        } else {
            b.appendChild(el('span', { id: 'pno-update-dltext', text: line }));
            const track = el('div', { style: 'flex:0 0 200px;height:7px;border-radius:7px;'
                + 'background:rgba(255,255,255,0.25);overflow:hidden' });
            track.appendChild(el('div', { id: 'pno-update-fill', style: `height:100%;width:${pct}%;background:#fff;`
                + 'border-radius:7px;transition:width 0.25s' }));
            b.appendChild(track);
        }
    } else if (phase === 'ready') {
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#16a34a,#22c55e)');
        const needsGrant = extra === true;
        b.appendChild(txt(needsGrant
            ? t('update.allow_installs', 'Allow this app to install updates on the settings screen that just opened, then tap Install again.')
            : t('update.ready', 'Update downloaded.')));
        const inst = mkBtn(t('update.install', 'Install'), true);
        inst.onclick = () => { install(); };
        b.appendChild(inst);
    } else if (phase === 'error') {
        const b = topBar(BAR_ID, 'linear-gradient(135deg,#dc2626,#ef4444)');
        b.appendChild(txt(t('update.error', 'Update failed. The game will try again next time.')));
        const dismiss = mkBtn(t('update.dismiss', 'Dismiss'), false);
        dismiss.onclick = () => {
            _phase = 'none';
            removeBar(BAR_ID);
            if (_update) render('offered'); // failed download is retryable
        };
        b.appendChild(dismiss);
    }
}
